import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { usePageState } from '../hooks/usePageState';
import { Contenuto, FormatoPost, StatoContenuto, TipoContenuto } from '../types';
import Filters from '../components/Filters';
import StatusBadge from '../components/StatusBadge';
import DriveFlagBadge from '../components/DriveFlagBadge';
import { Plus, Eye, Edit, Trash2, Calendar, CheckCircle, RefreshCw, ArrowLeft, FileText, Sparkles, Image, Video, BookOpen, LayoutTemplate, Palette, Film, Save, X, FileCheck, Megaphone, Archive, ArchiveRestore, Target, Folder, ExternalLink, ChevronRight, ChevronDown, AlertTriangle, Newspaper, ImageOff, CalendarDays, MessageSquareText, Camera } from 'lucide-react';
import AIImportModal from '../components/AIImportModal';
import CopywriterAssistant from '../components/CopywriterAssistant';
import NewsCompetitor from '../components/NewsCompetitor';
import ContatorePost from '../components/ContatorePost';
import CalendarioCliente from '../components/CalendarioCliente';
import CaptionMancanti from '../components/CaptionMancanti';
import { AnimatedModal } from '../components/AnimatedComponents';
import { Card } from '../components/ui/Card';
import { contenutiDaScrivere, postGraficiDelMese, targetPostGraficiMese } from '../utils/contenutiDaScrivere';
import { isPost, tipoDi, formatoDi, isCaroselloFoto, ETICHETTA_FORMATO, DESCRIZIONE_FORMATO } from '../utils/tipoContenuto';
import { accountDelCliente } from '../utils/accountDestinazione';
import AccountDestinazioneBadge from '../components/AccountDestinazioneBadge';
import Button from '../components/ui/Button';
import StatsCard from '../components/ui/StatsCard';

type CopywriterTab = 'current' | 'archived' | 'rubriche' | 'creative' | 'drive' | 'assistant' | 'intel' | 'calendario' | 'caption';

interface Rubrica {
  id: string;
  client_id: string;
  tipo: 'rubrica' | 'format';
  titolo: string;
  descrizione: string | null;
  // Struttura vincolante per l'AI: come devono essere gancio, corpo e CTA
  // dei contenuti di questa rubrica. Vuoti = la forma la decide il format.
  gancio: string | null;
  corpo: string | null;
  cta: string | null;
  created_at: string;
  updated_at: string;
}

interface CreativeForm {
  obiettivi: string;
  taglio: string;
  da_dire: string;
  da_non_dire: string;
  note: string;
}

// Elemento del Google Drive "Lista Clienti Social" (cartella o file).
interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  webViewLink: string | null;
  iconLink: string | null;
  thumbnailLink: string | null;
  modifiedTime: string | null;
  size: number | null;
}

const Copywriter: React.FC = () => {
  const { state, addContenuto, updateContenuto, deleteContenuto, updateContenutoStatus, unarchiveContenuto, confermaUscita, getClienteById, loadClienti, loadContenuti } = useData();
  const { getToken } = useAuth();
  const { forceRefresh, contenutiCount } = useAutoRefresh({ enabled: true, interval: 30000 });
  // Cliente aperto, tab e filtri vivono nell'URL: il tasto indietro del
  // browser e il refresh riportano esattamente al punto in cui si era.
  const [pageState, setPageState] = usePageState(
    { client: '', tab: 'current', q: '', from: '', to: '', edit: '' },
    { push: ['client', 'tab', 'edit'] }
  );
  const selectedClientId = pageState.client || null;
  const activeTab = pageState.tab as CopywriterTab;
  const searchTerm = pageState.q;
  const dateFrom = pageState.from;
  const dateTo = pageState.to;
  const setActiveTab = useCallback((tab: CopywriterTab) => setPageState({ tab }), [setPageState]);
  const setSearchTerm = useCallback((value: string) => setPageState({ q: value }), [setPageState]);
  const setDateFrom = useCallback((value: string) => setPageState({ from: value }), [setPageState]);
  const setDateTo = useCallback((value: string) => setPageState({ to: value }), [setPageState]);
  // Editor contenuto: 'new' oppure l'id del contenuto aperto. Sta nell'URL come
  // il resto, così il tasto indietro chiude la scheda invece di uscire dalla
  // pagina e un refresh non fa perdere il punto in cui si era.
  const editParam = pageState.edit;
  const isModalOpen = editParam !== '';
  const selectedContenuto = useMemo(
    () => (editParam && editParam !== 'new' ? state.contenuti.find(c => c.id === editParam) ?? null : null),
    [editParam, state.contenuti]
  );
  const [formData, setFormData] = useState({
    clienteId: '',
    tipo: 'reel' as TipoContenuto,
    formatoPost: 'grafica' as FormatoPost,
    accountDestinazione: [] as string[],
    titolo: '',
    hook: '',
    body: '',
    cta: '',
    copyPost: '',
    linkIspirazione: '',
    copertinaUrl: '',
    campagnaAdv: false
  });
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Upload handler condiviso per click e drag&drop
  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Il file deve essere un\'immagine (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Il file deve essere inferiore a 10MB');
      return;
    }
    try {
      setIsUploadingCover(true);
      const uploadData = new FormData();
      uploadData.append('image', file);
      const response = await fetch('/api/files/upload/image', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: uploadData
      });
      if (!response.ok) throw new Error('Upload fallito');
      const result = await response.json();
      if (result.success && result.data?.public_url) {
        setFormData(prev => ({ ...prev, copertinaUrl: result.data.public_url }));
      } else {
        throw new Error('Risposta non valida');
      }
    } catch (error) {
      console.error('Errore upload copertina:', error);
      alert('Errore nel caricamento della copertina. Riprova.');
    } finally {
      setIsUploadingCover(false);
    }
  };
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [isClearingFilters, setIsClearingFilters] = useState(false);
  // Drive clip: cartella del cliente sul Google Drive "Lista Clienti Social"
  const [driveMatched, setDriveMatched] = useState<boolean | null>(null);
  const [driveFolderName, setDriveFolderName] = useState<string | null>(null);
  const [driveFolderLink, setDriveFolderLink] = useState<string | null>(null);
  const [drivePath, setDrivePath] = useState<{ id: string; name: string }[]>([]);
  const [driveItems, setDriveItems] = useState<DriveItem[]>([]);
  const [isLoadingDrive, setIsLoadingDrive] = useState(false);
  const [driveError, setDriveError] = useState<string | null>(null);
  // Rubriche / Format del cliente
  const [rubriche, setRubriche] = useState<Rubrica[]>([]);
  const [isLoadingRubriche, setIsLoadingRubriche] = useState(false);
  const [isRubricaModalOpen, setIsRubricaModalOpen] = useState(false);
  const [selectedRubrica, setSelectedRubrica] = useState<Rubrica | null>(null);
  type RubricaForm = { tipo: 'rubrica' | 'format'; titolo: string; descrizione: string; gancio: string; corpo: string; cta: string };
  const RUBRICA_VUOTA: RubricaForm = { tipo: 'rubrica', titolo: '', descrizione: '', gancio: '', corpo: '', cta: '' };
  const [rubricaForm, setRubricaForm] = useState<RubricaForm>(RUBRICA_VUOTA);
  const [isSubmittingRubrica, setIsSubmittingRubrica] = useState(false);
  // Solo mobile: nel form contenuto il pannello Rubriche parte chiuso (mostra le sole
  // sigle) così i campi del testo restano subito raggiungibili. Da sm in su è sempre aperto.
  const [rubricheAperte, setRubricheAperte] = useState(false);
  // Linee guida Creative del cliente
  const [creativeForm, setCreativeForm] = useState<CreativeForm>({ obiettivi: '', taglio: '', da_dire: '', da_non_dire: '', note: '' });
  const [isLoadingCreative, setIsLoadingCreative] = useState(false);
  const [isSavingCreative, setIsSavingCreative] = useState(false);
  const [creativeSaved, setCreativeSaved] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  // I dati vengono caricati automaticamente dal DataProvider
  // Force refresh dei contenuti quando si monta il componente
  useEffect(() => {
    const refreshData = async () => {
      try {
        await loadContenuti();
      } catch (error) {
        console.error('Errore nel caricamento contenuti:', error);
      }
    };

    refreshData();
  }, []); // Rimuoviamo le dipendenze per evitare loop infiniti

  // AI Assistant a tutto schermo: Esc riporta al Copywriting e, finché è
  // aperto, la pagina sotto non scorre.
  useEffect(() => {
    if (activeTab !== 'assistant') return;
    const onKey = (e: KeyboardEvent) => {
      // Se c'è un modal aperto sopra la chat (es. "Scopri come ragiono"), Esc
      // chiude quello e basta: non anche l'assistente.
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"][aria-modal="true"]')) setActiveTab('current');
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [activeTab]);

  // Effetto per popolare il form quando si apre l'editor. La guardia su
  // formPopolatoPerRef evita che l'auto-refresh dei contenuti (ogni 30s)
  // sovrascriva quello che si sta scrivendo.
  const formPopolatoPerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editParam) {
      formPopolatoPerRef.current = null;
      return;
    }
    // contenuto non ancora arrivato dal server (es. dopo un refresh della pagina)
    if (editParam !== 'new' && !selectedContenuto) return;
    if (formPopolatoPerRef.current === editParam) return;
    formPopolatoPerRef.current = editParam;

    if (selectedContenuto) {
      setFormData({
        clienteId: selectedContenuto.clienteId || '',
        tipo: tipoDi(selectedContenuto),
        formatoPost: formatoDi(selectedContenuto) as FormatoPost,
        accountDestinazione: selectedContenuto.accountDestinazione || [],
        titolo: selectedContenuto.titolo || '',
        hook: selectedContenuto.hook || '',
        body: selectedContenuto.body || '',
        cta: selectedContenuto.cta || '',
        copyPost: selectedContenuto.copyPost || '',
        linkIspirazione: selectedContenuto.linkIspirazione || '',
        copertinaUrl: selectedContenuto.copertinaUrl || '',
        campagnaAdv: selectedContenuto.campagnaAdv || false
      });
    } else {
      // Nuovo contenuto — precompila sempre il cliente corrente (la casella
      // "Cliente" è nascosta nel form ma il valore resta necessario).
      setFormData({
        clienteId: selectedClientId || '',
        tipo: 'reel',
        formatoPost: 'grafica' as FormatoPost,
        accountDestinazione: [],
        titolo: '',
        hook: '',
        body: '',
        cta: '',
        copyPost: '',
        linkIspirazione: '',
        copertinaUrl: '',
        campagnaAdv: false
      });
    }
  }, [editParam, selectedContenuto, selectedClientId]);

  // Memoizza i contenuti filtrati per migliori performance e reattività
  const filteredContenuti = useMemo(() => {
    console.log(`🔍 Filtering ${state.contenuti.length} contenuti for activeTab: ${activeTab}`);

    // Debug: Show status breakdown of all content
    const statusBreakdown = state.contenuti.reduce((acc: Record<string, number>, content) => {
      acc[content.stato] = (acc[content.stato] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log('🔍 Status breakdown:', statusBreakdown);

    // Debug: Show archived contents specifically
    const archivedContentTitles = state.contenuti
      .filter(c => c.stato === StatoContenuto.ARCHIVIATO)
      .map(c => ({ titolo: c.titolo, stato: c.stato, clienteId: c.clienteId }));
    console.log('🔍 Contenuti archiviati in state:', archivedContentTitles);

    return state.contenuti.filter(contenuto => {
      // Only show content for selected client
      if (selectedClientId && contenuto.clienteId !== selectedClientId) {
        return false;
      }

      // Only show content from active clients
      const cliente = getClienteById(contenuto.clienteId);
      if (!cliente || cliente.status !== 'active') {
        return false;
      }

      // Filter by tab (current vs archived)
      if (activeTab === 'current') {
        if (contenuto.stato === StatoContenuto.PUBBLICATO || contenuto.stato === StatoContenuto.ARCHIVIATO) {
          console.log(`🔍 Hiding from current: ${contenuto.titolo} (stato: ${contenuto.stato})`);
          return false;
        }
      } else {
        if (contenuto.stato !== StatoContenuto.PUBBLICATO && contenuto.stato !== StatoContenuto.ARCHIVIATO) {
          console.log(`🔍 Hiding from archived: ${contenuto.titolo} (stato: ${contenuto.stato})`);
          return false;
        } else {
          console.log(`🔍 Showing in archived: ${contenuto.titolo} (stato: ${contenuto.stato})`);
        }
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (!cliente.nomeAzienda.toLowerCase().includes(searchLower) &&
            !contenuto.titolo.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Filter by date range
      if (dateFrom || dateTo) {
        const contentDate = new Date(contenuto.createdAt);

        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0); // Imposta l'ora a 00:00:00
          const contentDateForFrom = new Date(contenuto.createdAt);
          contentDateForFrom.setHours(0, 0, 0, 0); // Imposta l'ora a 00:00:00 per il confronto

          console.log('🔍 Filtro data da:', {
            contentDate: contentDateForFrom.toISOString(),
            fromDate: fromDate.toISOString(),
            isBefore: contentDateForFrom < fromDate
          });

          if (contentDateForFrom < fromDate) {
            return false;
          }
        }

        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // Imposta l'ora a 23:59:59
          const contentDateForTo = new Date(contenuto.createdAt);

          console.log('🔍 Filtro data a:', {
            contentDate: contentDateForTo.toISOString(),
            toDate: toDate.toISOString(),
            isAfter: contentDateForTo > toDate
          });

          if (contentDateForTo > toDate) {
            return false;
          }
        }
      }

      return true;
    }).sort((a, b) => {
      // Ordina per data di aggiornamento più recente
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [state.contenuti, searchTerm, dateFrom, dateTo, getClienteById, activeTab, selectedClientId, refreshTrigger]);


  // Funzione per pulire i filtri
  const handleClearFilters = async () => {
    if (isClearingFilters) return; // Previeni chiamate multiple

    try {
      setIsClearingFilters(true);
      setPageState({ q: '', from: '', to: '' });

      // Forza un refresh dei dati dopo aver pulito i filtri
      await loadContenuti();
    } catch (error) {
      console.error('Errore durante il refresh dopo pulizia filtri:', error);
    } finally {
      setIsClearingFilters(false);
    }
  };

  // Funzione per refresh manuale
  const handleManualRefresh = useCallback(async () => {
    if (isManualRefreshing) return;

    try {
      setIsManualRefreshing(true);
      await forceRefresh();
    } catch (error) {
      console.error('Errore durante il refresh manuale:', error);
    } finally {
      setIsManualRefreshing(false);
    }
  }, [forceRefresh, isManualRefreshing]);

  // Funzione per selezionare un cliente (voce di cronologia: il tasto indietro
  // riporta alla lista clienti)
  const handleSelectClient = (clientId: string) => {
    setPageState({ client: clientId, tab: 'current', q: '', from: '', to: '' });
  };

  // Funzione per tornare alla lista clienti
  const handleBackToClients = () => {
    setPageState({ client: '', tab: 'current', q: '', from: '', to: '' });
  };

  // Ricerca globale (vista lista clienti): cerca in TUTTI i contenuti dei
  // clienti attivi, non solo nel titolo, così un contenuto si ritrova anche
  // quando non ci si ricorda a quale cliente appartiene.
  const globalSearchResults = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q || selectedClientId) return [];

    return state.contenuti
      .filter(contenuto => {
        const cliente = getClienteById(contenuto.clienteId);
        if (!cliente || cliente.status !== 'active') return false;
        return [
          cliente.nomeAzienda,
          contenuto.titolo,
          contenuto.hook,
          contenuto.body,
          contenuto.cta,
          contenuto.copyPost,
          contenuto.note
        ].some(campo => campo?.toLowerCase().includes(q));
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 50);
  }, [state.contenuti, searchTerm, selectedClientId, getClienteById]);

  // Apre un risultato: entra nel cliente giusto, sulla tab giusta (archiviati o
  // attuali a seconda dello stato) e apre direttamente la scheda del contenuto.
  const handleOpenSearchResult = (contenuto: Contenuto) => {
    const isArchiviato =
      contenuto.stato === StatoContenuto.PUBBLICATO || contenuto.stato === StatoContenuto.ARCHIVIATO;
    setPageState({
      client: contenuto.clienteId,
      tab: isArchiviato ? 'archived' : 'current',
      q: '',
      from: '',
      to: '',
      edit: contenuto.id
    });
  };

  // ---- Rubriche / Format ----
  const loadRubriche = useCallback(async (clientId: string) => {
    try {
      setIsLoadingRubriche(true);
      const response = await fetch(`/api/rubriche?client_id=${clientId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRubriche(data.data || []);
      } else {
        console.error('Errore nel caricamento rubriche:', response.status);
      }
    } catch (error) {
      console.error('Errore nel caricamento rubriche:', error);
    } finally {
      setIsLoadingRubriche(false);
    }
  }, [getToken]);

  // La scheda «Calendario» compare solo per i clienti che hanno un calendario collegato
  const [haCalendario, setHaCalendario] = useState(false);
  useEffect(() => {
    setHaCalendario(false);
    if (!selectedClientId) return;
    let annullato = false;
    fetch(`/api/client-calendar?client_id=${selectedClientId}`, { headers: { 'Authorization': `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!annullato) setHaCalendario(!!j?.data?.collegato); })
      .catch(() => {});
    return () => { annullato = true; };
  }, [selectedClientId, getToken]);

  // Quante caption mancano ai contenuti che stanno per uscire (pubblica le
  // prende da qui così come sono): il numero sta sulla scheda «Caption
  // mancanti». I clienti con i testi scritti dal cliente non ce l'hanno.
  const [captionMancanti, setCaptionMancanti] = useState<number | null>(null);
  useEffect(() => {
    setCaptionMancanti(null);
    if (!selectedClientId) return;
    let annullato = false;
    fetch(`/api/ai/caption-mancanti?client_id=${selectedClientId}`, { headers: { 'Authorization': `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!annullato && j?.success) setCaptionMancanti(j.data.contenuti.length); })
      .catch(() => {});
    return () => { annullato = true; };
  }, [selectedClientId, getToken]);

  // Carica le rubriche quando si apre il relativo tab oppure il form Nuovo/Modifica
  // Contenuto (servono per mostrare i tag rubriche affianco al titolo).
  useEffect(() => {
    if (selectedClientId && (activeTab === 'rubriche' || isModalOpen)) {
      loadRubriche(selectedClientId);
    }
  }, [selectedClientId, activeTab, isModalOpen, loadRubriche]);

  const handleNewRubrica = () => {
    setSelectedRubrica(null);
    setRubricaForm(RUBRICA_VUOTA);
    setIsRubricaModalOpen(true);
  };

  const handleEditRubrica = (rubrica: Rubrica) => {
    setSelectedRubrica(rubrica);
    setRubricaForm({ tipo: rubrica.tipo, titolo: rubrica.titolo, descrizione: rubrica.descrizione || '', gancio: rubrica.gancio || '', corpo: rubrica.corpo || '', cta: rubrica.cta || '' });
    setIsRubricaModalOpen(true);
  };

  const handleCloseRubricaModal = () => {
    setIsRubricaModalOpen(false);
    setSelectedRubrica(null);
  };

  const handleSubmitRubrica = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRubrica || !selectedClientId) return;
    try {
      setIsSubmittingRubrica(true);
      const isEdit = selectedRubrica !== null;
      const url = isEdit ? `/api/rubriche/${selectedRubrica!.id}` : '/api/rubriche';
      const method = isEdit ? 'PUT' : 'POST';
      const campi = {
        tipo: rubricaForm.tipo,
        titolo: rubricaForm.titolo,
        descrizione: rubricaForm.descrizione || null,
        gancio: rubricaForm.gancio.trim() || null,
        corpo: rubricaForm.corpo.trim() || null,
        cta: rubricaForm.cta.trim() || null,
      };
      const body = isEdit ? campi : { client_id: selectedClientId, ...campi };
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setIsRubricaModalOpen(false);
      setSelectedRubrica(null);
      await loadRubriche(selectedClientId);
    } catch (error) {
      console.error('Errore nel salvare la rubrica:', error);
      alert('Errore nel salvare la rubrica. Riprova.');
    } finally {
      setIsSubmittingRubrica(false);
    }
  };

  const handleDeleteRubrica = async (id: string) => {
    if (!selectedClientId) return;
    if (!window.confirm('Sei sicuro di voler eliminare questa rubrica?')) return;
    try {
      const response = await fetch(`/api/rubriche/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      await loadRubriche(selectedClientId);
    } catch (error) {
      console.error('Errore nell\'eliminazione della rubrica:', error);
      alert('Errore nell\'eliminazione della rubrica. Riprova.');
    }
  };

  // ---- Creative / Linee guida ----
  const loadCreative = useCallback(async (clientId: string) => {
    try {
      setIsLoadingCreative(true);
      const response = await fetch(`/api/creative?client_id=${clientId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        const c = data.data;
        setCreativeForm({
          obiettivi: c?.obiettivi || '',
          taglio: c?.taglio || '',
          da_dire: c?.da_dire || '',
          da_non_dire: c?.da_non_dire || '',
          note: c?.note || ''
        });
      } else {
        console.error('Errore nel caricamento creative:', response.status);
      }
    } catch (error) {
      console.error('Errore nel caricamento creative:', error);
    } finally {
      setIsLoadingCreative(false);
    }
  }, [getToken]);

  // Carica le linee guida creative quando si apre il relativo tab
  useEffect(() => {
    if (selectedClientId && activeTab === 'creative') {
      loadCreative(selectedClientId);
    }
  }, [selectedClientId, activeTab, loadCreative]);

  // ---- Drive clip ----
  // Risolve e carica la cartella Drive del cliente (dal nome azienda).
  const loadDriveRoot = useCallback(async (companyName: string) => {
    try {
      setIsLoadingDrive(true);
      setDriveError(null);
      const response = await fetch(`/api/drive/client?name=${encodeURIComponent(companyName)}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Errore Drive');
      const d = data.data;
      setDriveMatched(!!d.matched);
      if (d.matched && d.folder) {
        setDriveFolderName(d.folder.name);
        setDriveFolderLink(d.folder.webViewLink || null);
        setDrivePath([{ id: d.folder.id, name: d.folder.name }]);
        setDriveItems(d.items || []);
      } else {
        setDriveFolderName(null);
        setDriveFolderLink(null);
        setDrivePath([]);
        setDriveItems([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento Drive:', error);
      setDriveError('Impossibile leggere il Drive. Riprova.');
      setDriveMatched(null);
    } finally {
      setIsLoadingDrive(false);
    }
  }, [getToken]);

  // Naviga dentro una sottocartella (o torna a un livello del breadcrumb).
  const openDriveFolder = useCallback(async (folder: { id: string; name: string }, depth?: number) => {
    try {
      setIsLoadingDrive(true);
      setDriveError(null);
      const response = await fetch(`/api/drive/folder?id=${encodeURIComponent(folder.id)}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Errore Drive');
      setDriveItems(data.data.items || []);
      setDrivePath(prev => {
        // depth definito = click su breadcrumb: tronca fin lì; altrimenti push.
        if (typeof depth === 'number') return prev.slice(0, depth + 1);
        return [...prev, folder];
      });
    } catch (error) {
      console.error('Errore nel caricamento cartella Drive:', error);
      setDriveError('Impossibile leggere la cartella. Riprova.');
    } finally {
      setIsLoadingDrive(false);
    }
  }, [getToken]);

  // All'apertura del tab (o al cambio cliente) ricarica la radice della cartella cliente.
  useEffect(() => {
    if (selectedClientId && activeTab === 'drive') {
      const nome = getClienteById(selectedClientId)?.nomeAzienda;
      if (nome) loadDriveRoot(nome);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, activeTab, loadDriveRoot]);

  const updateCreativeField = (field: keyof CreativeForm, value: string) => {
    setCreativeForm(prev => ({ ...prev, [field]: value }));
    setCreativeSaved(false);
  };

  const handleSaveCreative = async () => {
    if (isSavingCreative || !selectedClientId) return;
    try {
      setIsSavingCreative(true);
      const response = await fetch('/api/creative', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          client_id: selectedClientId,
          obiettivi: creativeForm.obiettivi || null,
          taglio: creativeForm.taglio || null,
          da_dire: creativeForm.da_dire || null,
          da_non_dire: creativeForm.da_non_dire || null,
          note: creativeForm.note || null
        })
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setCreativeSaved(true);
    } catch (error) {
      console.error('Errore nel salvare le linee guida creative:', error);
      alert('Errore nel salvare le linee guida creative. Riprova.');
    } finally {
      setIsSavingCreative(false);
    }
  };

  // Funzione per aprire il modal per nuovo contenuto
  const handleNewContent = () => {
    setPageState({ edit: 'new' });
  };

  // Funzione per aprire il modal per modificare contenuto
  const handleEditContent = (contenuto: Contenuto) => {
    setPageState({ edit: contenuto.id });
  };

  // Funzione per segnare il contenuto come pronto
  const handleMarkAsReady = useCallback(async (contenuto: Contenuto) => {
    if (window.confirm('Sei sicuro di voler segnare questo contenuto come pronto?')) {
      try {
        await updateContenutoStatus(contenuto.id, StatoContenuto.TESTO_PRONTO);
        // Dopo l'aggiornamento, ricarica i contenuti per assicurare la sincronizzazione
        await loadContenuti();
        // Triggera il re-render della lista
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('Errore nel segnare il contenuto come pronto:', error);
        alert('Errore nel segnare il contenuto come pronto. Riprova.');
      }
    }
  }, [updateContenutoStatus, loadContenuti, setRefreshTrigger]);

  // Manda un contenuto in archivio: sparisce dal tab "Attuali" e finisce tra gli
  // "Script Archiviati", da dove può sempre essere ripristinato.
  const handleArchive = useCallback(async (contenuto: Contenuto) => {
    if (!window.confirm(`Mettere "${contenuto.titolo}" nell'archivio? Lo ritrovi nel tab "Script Archiviati".`)) {
      return;
    }
    try {
      // Niente notes: il campo è dell'utente e verrebbe sovrascritto
      await updateContenutoStatus(contenuto.id, StatoContenuto.ARCHIVIATO);
      await loadContenuti();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Errore nell\'archiviazione del contenuto:', error);
    }
  }, [updateContenutoStatus, loadContenuti, setRefreshTrigger]);

  // Toglie un contenuto dall'archivio: torna nel tab "Attuali" come "Testo pronto",
  // senza data di pubblicazione, pronto per essere ri-registrato/riprogrammato.
  const handleUnarchive = useCallback(async (contenuto: Contenuto) => {
    if (!window.confirm(`Togliere "${contenuto.titolo}" dall'archivio? Tornerà tra i contenuti attuali come "Testo confermato".`)) {
      return;
    }
    try {
      await unarchiveContenuto(contenuto.id);
      await loadContenuti();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Errore nel ripristino dall\'archivio:', error);
    }
  }, [unarchiveContenuto, loadContenuti, setRefreshTrigger]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // Previeni doppi submit
    // Editor aperto su un contenuto esistente ma dati non ancora caricati (es.
    // subito dopo un refresh della pagina): salvare ora creerebbe un doppione.
    if (editParam !== 'new' && !selectedContenuto) return;

    console.log('🚀 Tentativo di salvataggio:', { selectedContenuto, formData });

    // Post grafico: solo copy + immagine. Obbligatorio è il copy; l'immagine
    // può arrivare dopo (si salva senza e la card lo segnala). Niente
    // hook/corpo/CTA (non si spediscono nemmeno) e niente Campagna ADV.
    const isPostGrafico = formData.tipo === 'post';
    if (isPostGrafico && !formData.copyPost.trim()) {
      alert(`Il ${ETICHETTA_FORMATO[formData.formatoPost].toLowerCase()} deve avere il copy`);
      return;
    }

    try {
      setIsSubmitting(true);

      if (selectedContenuto) {
        // Modifica contenuto esistente
        await updateContenuto(selectedContenuto.id, {
          tipo: formData.tipo,
          ...(isPostGrafico ? { formatoPost: formData.formatoPost } : {}),
          accountDestinazione: formData.accountDestinazione,
          titolo: formData.titolo,
          ...(isPostGrafico
            ? {}
            : { hook: formData.hook, body: formData.body, cta: formData.cta }),
          copyPost: formData.copyPost || undefined,
          linkIspirazione: formData.linkIspirazione || undefined,
          copertinaUrl: formData.copertinaUrl || undefined,
          campagnaAdv: isPostGrafico ? false : formData.campagnaAdv
        });
      } else {
        // Nuovo contenuto
        const nuovoContenuto: Omit<Contenuto, 'id' | 'createdAt' | 'updatedAt'> = {
          clienteId: formData.clienteId,
          tipo: formData.tipo,
          formatoPost: isPostGrafico ? formData.formatoPost : undefined,
          accountDestinazione: formData.accountDestinazione,
          titolo: formData.titolo,
          hook: isPostGrafico ? '' : formData.hook,
          body: isPostGrafico ? '' : formData.body,
          cta: isPostGrafico ? '' : formData.cta,
          copyPost: formData.copyPost || undefined,
          linkIspirazione: formData.linkIspirazione || undefined,
          copertinaUrl: formData.copertinaUrl || undefined,
          stato: StatoContenuto.BOZZA,
          videoRegistrato: false,
          campagnaAdv: isPostGrafico ? false : formData.campagnaAdv
        };
        await addContenuto(nuovoContenuto);
      }

      // A questo punto il salvataggio è andato a buon fine: chiudi e resetta
      // SUBITO, prima del refresh. In passato un loadContenuti() fallito (es.
      // GET lungo in timeout su mobile) finiva nel catch e mostrava un falso
      // "Errore nel salvare" pur avendo salvato davvero.
      setFormData({
        clienteId: '',
        tipo: 'reel',
        formatoPost: 'grafica' as FormatoPost,
        accountDestinazione: [],
        titolo: '',
        hook: '',
        body: '',
        cta: '',
        copyPost: '',
        linkIspirazione: '',
        copertinaUrl: '',
        campagnaAdv: false
      });

      setPageState({ edit: '' });
    } catch (error) {
      console.error('Errore nel salvare il contenuto:', error);
      alert('Errore nel salvare il contenuto. Riprova.');
    } finally {
      setIsSubmitting(false);
    }

    // Refresh della lista DOPO il salvataggio, fuori dal try principale: se
    // fallisce non deve mai apparire come errore di salvataggio.
    try {
      await loadContenuti();
      setRefreshTrigger(prev => prev + 1);
    } catch (refreshError) {
      console.warn('Refresh contenuti fallito dopo il salvataggio (contenuto già salvato):', refreshError);
    }
  }, [isSubmitting, editParam, selectedContenuto, formData, updateContenuto, addContenuto, loadContenuti, setPageState]);

  // Modal close handler
  const handleCloseModal = () => {
    setPageState({ edit: '' });
  };

  // Se nessun cliente selezionato, mostra lista clienti
  if (!selectedClientId) {
    // Totale contenuti ancora da scrivere su tutti i clienti attivi (questo mese)
    const nowList = new Date();
    const meseCorrente = nowList.getMonth();
    const annoCorrente = nowList.getFullYear();
    const queryRicerca = searchTerm.trim().toLowerCase();
    const clientiAttivi = state.clienti.filter(c => c.status === 'active');
    // Con la ricerca attiva la griglia mostra solo i clienti col nome che combacia:
    // i contenuti trovati stanno nel blocco risultati qui sopra.
    const clientiVisibili = queryRicerca
      ? clientiAttivi.filter(c => c.nomeAzienda.toLowerCase().includes(queryRicerca))
      : clientiAttivi;

    // I clienti che si scrivono i testi da soli non entrano nel totale.
    const totaleDaScrivere = clientiAttivi
      .reduce(
        (tot, cliente) =>
          tot +
          contenutiDaScrivere({
            contenuti: state.contenuti,
            clienteId: cliente.id,
            targetMensile: cliente.reelMensili || 0,
            testiDalCliente: cliente.testiDalCliente,
            mese: meseCorrente,
            anno: annoCorrente,
          }),
        0
      );

    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-slate-900 dark:text-slate-100 break-words" style={{ letterSpacing: '-0.02em' }}>
              Copywriting
            </h1>
            <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Contenuti da scrivere per i clienti
            </p>
          </div>
          <StatsCard
            value={totaleDaScrivere}
            label="contenuti da scrivere"
            variant="warning"
          />
        </div>

        {/* Ricerca globale su tutti i contenuti */}
        <Filters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Cerca un contenuto per titolo, hook, testo o cliente..."
          onClearFilters={handleClearFilters}
        />

        {queryRicerca && (
          <div className="mb-4 sm:mb-6">
            <div className="mb-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {globalSearchResults.length === 0
                ? 'Nessun contenuto trovato'
                : `${globalSearchResults.length} contenuti trovati`}
            </div>

            <div className="space-y-2">
              {globalSearchResults.map((contenuto) => {
                const clienteRisultato = getClienteById(contenuto.clienteId);
                return (
                  <button
                    key={contenuto.id}
                    onClick={() => handleOpenSearchResult(contenuto)}
                    className="w-full text-left p-3 sm:p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">
                          {contenuto.titolo}
                        </h4>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                          {clienteRisultato?.nomeAzienda || 'Cliente non trovato'}
                        </p>
                        {contenuto.hook && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                            {contenuto.hook}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <StatusBadge stato={contenuto.stato} size="xs" />
                        <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                          {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Lista Clienti */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {clientiVisibili.length === 0 ? (
            <div className="col-span-full">
              <Card className="p-4 sm:p-6 md:p-8 text-center">
                <FileText className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
                <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900" style={{ letterSpacing: '-0.02em' }}>
                  {queryRicerca ? 'Nessun cliente con questo nome' : 'Nessun cliente attivo'}
                </h3>
                <p className="mt-1 text-sm sm:text-base text-slate-500">
                  {queryRicerca ? 'Prova a cercare il titolo del contenuto' : 'I clienti attivi appariranno qui'}
                </p>
              </Card>
            </div>
          ) : (
            clientiVisibili
              .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }))
              .map((cliente) => {
              // Contenuti da scrivere: calcolo condiviso con la Dashboard
              // (vedi utils/contenutiDaScrivere.ts).
              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();

              const targetMensile = cliente.reelMensili || 0;
              const testiDalCliente = !!cliente.testiDalCliente;
              const daScrivere = contenutiDaScrivere({
                contenuti: state.contenuti,
                clienteId: cliente.id,
                targetMensile,
                testiDalCliente,
                mese: currentMonth,
                anno: currentYear,
              });

              return (
                <div
                  key={cliente.id}
                  onClick={() => handleSelectClient(cliente.id)}
                  className={`block p-3 sm:p-4 rounded-xl border transition-all duration-200 hover:shadow-md cursor-pointer bg-white dark:bg-gray-800 ${
                    daScrivere === 0
                      ? 'border-emerald-400/70 dark:border-emerald-500/50 ring-1 ring-emerald-400/15 hover:border-emerald-400 dark:hover:border-emerald-500'
                      : 'border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    {/* Logo cliente */}
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                      {cliente.logoUrl ? (
                        <img
                          src={cliente.logoUrl}
                          alt={cliente.nomeAzienda}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-xs sm:text-sm font-medium">
                          {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                        {cliente.nomeAzienda}
                      </h4>
                      <div className="flex items-center gap-1 mt-0.5 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                        <Target className="w-3 h-3 flex-shrink-0" />
                        <span><span className="font-semibold text-slate-700 dark:text-slate-300">{targetMensile}</span> reel previsti</span>
                      </div>
                      {/* Post grafici e caroselli foto: scritti nel mese su quelli previsti (settimanali × 4) */}
                      <ContatorePost cliente={cliente} contenuti={state.contenuti} mese={currentMonth} anno={currentYear} />
                    </div>
                  </div>

                  {/* Da scrivere — giallo; verde quando non c'è nulla da scrivere.
                      Per i clienti che si scrivono i testi da soli lo diciamo
                      esplicitamente, così non sembra lavoro finito da noi. */}
                  {testiDalCliente ? (
                    <div
                      className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 px-2.5 py-1.5 flex items-center justify-between"
                      title="I testi li scrive il cliente: non c'è nulla da scrivere da parte nostra"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" /> Testi dal cliente
                      </div>
                      <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    </div>
                  ) : daScrivere === 0 ? (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 px-2.5 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> Completato
                      </div>
                      <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    </div>
                  ) : (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40 px-2.5 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-amber-700 dark:text-amber-400">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" /> Da scrivere
                      </div>
                      <div className="text-base sm:text-lg font-bold text-amber-600 dark:text-amber-400 leading-none">{daScrivere}</div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // Se cliente selezionato, mostra i suoi contenuti
  const selectedClient = getClienteById(selectedClientId);

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {!isModalOpen && (
      <>
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className="space-y-3 sm:space-y-4 md:space-y-0 md:flex md:justify-between md:items-center">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <button
                onClick={handleBackToClients}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Torna ai clienti"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 truncate" style={{ letterSpacing: '-0.02em' }}>
                  {selectedClient?.nomeAzienda}
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-slate-600 truncate">
                  Contenuti per {selectedClient?.nomeAzienda} ({filteredContenuti.length} totali)
                </p>
              </div>
            </div>
          </div>
          {/* Mobile: unica riga [aggiorna][AI][Nuovo], niente bottoni impilati a tutta larghezza */}
          <div className="flex flex-row items-center gap-2">
            <Button
              onClick={handleManualRefresh}
              disabled={isManualRefreshing}
              variant="secondary"
              className="min-h-[44px] w-auto flex-shrink-0 px-3 sm:px-4 text-sm sm:text-base"
            >
              <RefreshCw size={18} className={`sm:w-5 sm:h-5 ${isManualRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isManualRefreshing ? 'Aggiornando...' : 'Aggiorna'}</span>
              <span className="sm:hidden">{isManualRefreshing ? '...' : ''}</span>
            </Button>
            {(activeTab === 'current' || activeTab === 'archived') && (
              <>
                <Button
                  onClick={() => setIsAIModalOpen(true)}
                  variant="secondary"
                  className="min-h-[44px] w-auto flex-shrink-0 px-3 sm:px-4 text-sm sm:text-base"
                >
                  <Sparkles size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Import con AI</span>
                  <span className="sm:hidden">AI</span>
                </Button>
                <Button
                  onClick={handleNewContent}
                  variant="primary"
                  className="min-h-[44px] flex-1 sm:flex-none sm:w-auto text-sm sm:text-base"
                >
                  <Plus size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Nuovo Contenuto</span>
                  <span className="sm:hidden">Nuovo</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-slate-200 relative">
          {/* Mobile: strip scorrevole (i tab non si comprimono più uno sull'altro) */}
          <nav className="-mb-px flex space-x-5 sm:space-x-8 overflow-x-auto scrollbar-hide pr-8 sm:pr-0">
            <button
              onClick={() => setActiveTab('current')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'current'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Script Attuali
            </button>
            <button
              onClick={() => setActiveTab('assistant')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                activeTab === 'assistant'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Sparkles size={14} />
              AI Assistant
            </button>
            <button
              onClick={() => setActiveTab('intel')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                activeTab === 'intel'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Newspaper size={14} />
              News & Competitor
            </button>
            {haCalendario && (
              <button
                onClick={() => setActiveTab('calendario')}
                className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                  activeTab === 'calendario'
                    ? 'border-[#fdf751] text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <CalendarDays size={14} />
                Calendario
              </button>
            )}
            {!selectedClient?.testiDalCliente && (
              <button
                onClick={() => setActiveTab('caption')}
                className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                  activeTab === 'caption'
                    ? 'border-[#fdf751] text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <MessageSquareText size={14} />
                Caption mancanti
                {captionMancanti !== null && captionMancanti > 0 && (
                  <span className="ml-0.5 rounded-full px-1.5 text-[11px] font-semibold leading-[18px]" style={{ background: '#FDF751', color: '#13161C' }}>
                    {captionMancanti}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setActiveTab('rubriche')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                activeTab === 'rubriche'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <BookOpen size={14} />
              Rubriche
            </button>
            <button
              onClick={() => setActiveTab('creative')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                activeTab === 'creative'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Palette size={14} />
              Linee Guida
            </button>
            <button
              onClick={() => setActiveTab('drive')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                activeTab === 'drive'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Film size={14} />
              Drive clip
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`flex-shrink-0 py-2.5 sm:py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'archived'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Script Archiviati
            </button>
          </nav>
          {/* Solo mobile: sfumatura a destra, per far capire che i tab scorrono */}
          <div className="sm:hidden pointer-events-none absolute right-0 top-0 bottom-px w-10 bg-gradient-to-l from-[#fafafb] to-transparent" />
        </div>
      </div>

      {(activeTab === 'current' || activeTab === 'archived') && (
      <>
      {/* Filters */}
      <Filters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
        searchPlaceholder="Cerca per cliente o titolo..."
        onClearFilters={handleClearFilters}
      />

      {/* Content List */}
      <div className="mb-3 sm:mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 text-xs sm:text-sm text-slate-500">
          <span>Risultati trovati: {filteredContenuti.length}</span>
          <span className="hidden sm:inline">Dati aggiornati automaticamente ogni 30 secondi</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {filteredContenuti.length === 0 ? (
          <div className="col-span-full">
            <Card className="p-4 sm:p-6 md:p-8 text-center">
              <Calendar className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900" style={{ letterSpacing: '-0.02em' }}>
                {activeTab === 'current' ? 'Nessun contenuto attuale trovato' : 'Nessun script archiviato trovato'}
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                {activeTab === 'current'
                  ? 'Inizia creando il tuo primo contenuto'
                  : 'Gli script pubblicati e quelli programmati scaduti appariranno qui automaticamente'
                }
              </p>
            </Card>
          </div>
        ) : (
          filteredContenuti.map((contenuto) => (
            <Card
              key={contenuto.id}
              className="p-3 sm:p-4 md:p-6 flex flex-col h-full"
            >
              <div className="mb-3 sm:mb-4">
                <div className="flex flex-row flex-wrap justify-between items-center text-[10px] sm:text-xs text-slate-400 mb-2 gap-x-3 gap-y-0.5">
                  <span>Creato: {new Date(contenuto.createdAt).toLocaleDateString('it-IT')}</span>
                  <span>Agg: {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}</span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>{contenuto.titolo}</h3>
                  <p className="text-xs sm:text-sm text-slate-500">
                    {getClienteById(contenuto.clienteId)?.nomeAzienda || 'Cliente non trovato'}
                  </p>
                </div>
              </div>
              {/* flex-1 + mt-auto sulle azioni: i pulsanti stanno in fondo alla card, alla stessa altezza in tutta la riga */}
              <div className="flex-1 flex flex-col gap-2 sm:gap-3">
                {isPost(contenuto) ? (
                  contenuto.copyPost && (
                    <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 line-clamp-3">
                      {contenuto.copyPost}
                    </div>
                  )
                ) : contenuto.hook && (
                  <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    {contenuto.hook}
                  </div>
                )}
                {/* Indicatori media */}
                {(contenuto.copertinaUrl || contenuto.linkIspirazione || contenuto.copyPost) && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {contenuto.linkIspirazione && (
                      <a href={contenuto.linkIspirazione} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                        <FileText size={10} /> Ispirazione
                      </a>
                    )}
                    {contenuto.copertinaUrl && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-50 text-purple-700">
                        <Image size={10} /> Copertina
                      </span>
                    )}
                    {contenuto.copyPost && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-50 text-amber-700">
                        <FileText size={10} /> Copy
                      </span>
                    )}
                  </div>
                )}
                {/* Uscita mancata: l'archiviazione automatica non l'ha trovato
                    uscito (non montato o assente da Instagram) e l'ha rimesso qui. */}
                {contenuto.uscitaMancata && contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                  <div className="mb-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2 text-[11px] sm:text-xs text-red-400">
                    <div className="flex items-start gap-1.5">
                      <AlertTriangle size={13} className="flex-shrink-0 mt-px" />
                      <span>
                        <strong className="font-semibold">Non risulta uscito.</strong>{' '}
                        {contenuto.uscitaMancataMotivo
                          ? contenuto.uscitaMancataMotivo.charAt(0).toUpperCase() + contenuto.uscitaMancataMotivo.slice(1)
                          : `Doveva uscire il ${contenuto.uscitaMancata.toLocaleDateString('it-IT')}`}
                        . Riprogrammalo, oppure segnalo come uscito.
                      </span>
                    </div>
                    <div className="mt-1.5 flex justify-end">
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`"${contenuto.titolo}" è uscito davvero? Va in archivio con la data ${contenuto.uscitaMancata!.toLocaleDateString('it-IT')}.`)) return;
                          try { await confermaUscita(contenuto.id); } catch { /* toast già mostrato */ }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold border border-red-500/40 hover:bg-red-500/15 transition-colors"
                      >
                        <CheckCircle size={12} /> È uscito
                      </button>
                    </div>
                  </div>
                )}
                {/* Badge sopra, azioni centrate in fondo */}
                <div className="mt-auto pt-2 flex flex-col gap-3 sm:gap-4">
                  {/* Badge stato + tag aggiuntivi */}
                  <div className="flex flex-row flex-wrap items-center justify-center gap-1.5">
                    <AccountDestinazioneBadge contenuto={contenuto} cliente={state.clienti.find(c => c.id === contenuto.clienteId)} />
                    {isPost(contenuto) && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-purple-500/15 text-purple-300 border border-purple-500/30 tracking-wide"
                        title={DESCRIZIONE_FORMATO[formatoDi(contenuto)]}>
                        {isCaroselloFoto(contenuto) ? <Camera size={11} className="flex-shrink-0" /> : <Image size={11} className="flex-shrink-0" />}
                        {ETICHETTA_FORMATO[formatoDi(contenuto)]}
                      </span>
                    )}
                    {isPost(contenuto) && !contenuto.copertinaUrl && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30 tracking-wide"
                        title="Il post è salvato senza grafica: va caricata prima di pubblicarlo">
                        <ImageOff size={11} className="flex-shrink-0" />
                        Senza immagine
                      </span>
                    )}
                    <DriveFlagBadge contenuto={contenuto} grande />
                    {contenuto.campagnaAdv && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-pink-500/15 text-pink-300 border border-pink-500/30 tracking-wide"
                        title="Contenuto Campagna ADV, non un classico reel">
                        <Megaphone size={11} className="flex-shrink-0" />
                        Campagna ADV
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium tracking-wide flex-shrink-0 border ${
                      contenuto.stato === StatoContenuto.BOZZA       ? 'bg-slate-100 text-slate-600 border-slate-200' :
                      contenuto.stato === StatoContenuto.TESTO_PRONTO ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                      contenuto.stato === StatoContenuto.REGISTRATO   ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' :
                      contenuto.stato === StatoContenuto.MONTATO      ? 'bg-violet-500/15 text-violet-300 border-violet-500/30' :
                      contenuto.stato === StatoContenuto.PROGRAMMATO  ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                      contenuto.stato === StatoContenuto.PUBBLICATO   ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' :
                      'bg-red-500/15 text-red-300 border-red-500/30'
                    }`}>
                      {contenuto.stato === StatoContenuto.TESTO_PRONTO && <FileCheck size={11} className="flex-shrink-0" />}
                      {contenuto.stato === StatoContenuto.PROGRAMMATO && <Calendar size={11} className="flex-shrink-0" />}
                      {contenuto.stato === StatoContenuto.ARCHIVIATO && <Archive size={11} className="flex-shrink-0" />}
                      {contenuto.stato === StatoContenuto.BOZZA ? 'Bozza' :
                       contenuto.stato === StatoContenuto.TESTO_PRONTO ? 'Testo confermato' :
                       contenuto.stato === StatoContenuto.REGISTRATO ? 'Registrato' :
                       contenuto.stato === StatoContenuto.MONTATO ? 'Montato' :
                       contenuto.stato === StatoContenuto.PROGRAMMATO ? 'Programmato' :
                       contenuto.stato === StatoContenuto.PUBBLICATO ? 'Pubblicato' :
                       contenuto.stato === StatoContenuto.ARCHIVIATO ? 'Archiviato' :
                       contenuto.stato}
                    </span>
                    {contenuto.videoRegistrato &&
                     contenuto.stato !== StatoContenuto.PROGRAMMATO &&
                     contenuto.stato !== StatoContenuto.PUBBLICATO &&
                     contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30 tracking-wide"
                        title="Video già registrato, non ancora programmato">
                        <Video size={11} className="flex-shrink-0" />
                        Registrato
                      </span>
                    )}
                    {contenuto.videoRegistrato &&
                     contenuto.videoMontato &&
                     contenuto.stato !== StatoContenuto.PROGRAMMATO &&
                     contenuto.stato !== StatoContenuto.PUBBLICATO &&
                     contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 tracking-wide"
                        title="Video montato (pagina Editing), non ancora programmato">
                        <Film size={11} className="flex-shrink-0" />
                        Montato
                      </span>
                    )}
                    {activeTab === 'archived' && contenuto.stato === StatoContenuto.PUBBLICATO && (
                      <span className="text-[10px] sm:text-xs text-slate-500">
                        Mandato in produzione
                      </span>
                    )}
                    {contenuto.clientModifiedAt && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-orange-500/15 text-orange-300 border border-orange-500/30 tracking-wide"
                        title={`Modificato dal cliente il ${new Date(contenuto.clientModifiedAt).toLocaleDateString('it-IT')} alle ${new Date(contenuto.clientModifiedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`}>
                        <Edit size={11} className="flex-shrink-0" />
                        Modificato dal cliente
                      </span>
                    )}
                  </div>
                  {/* Azioni: "Segna come pronto" (se bozza) in alto, Modifica + Archivia + Elimina sotto */}
                  <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:min-w-[220px] sm:mx-auto">
                    {contenuto.stato === StatoContenuto.ARCHIVIATO && (
                      <button
                        onClick={() => handleUnarchive(contenuto)}
                        className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0 col-span-2 sm:w-full"
                        title="Togli dall'archivio: torna tra i contenuti attuali come Testo pronto"
                      >
                        <ArchiveRestore size={12} className="flex-shrink-0" />
                        <span className="hidden sm:inline">Togli dall'archivio</span>
                        <span className="sm:hidden">Ripristina</span>
                      </button>
                    )}
                    {/* Post grafico tolto dalla coda col pulsante "Rimuovi" di
                        Pubblicazione: i reel ci tornano da Riprese/Editing, il post
                        non passa di lì, quindi si rimanda in coda da qui. */}
                    {isPost(contenuto) && contenuto.rimossoDaProgrammazione &&
                     contenuto.stato !== StatoContenuto.BOZZA &&
                     contenuto.stato !== StatoContenuto.PROGRAMMATO &&
                     contenuto.stato !== StatoContenuto.PUBBLICATO &&
                     contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                      <button
                        onClick={async () => {
                          try {
                            await updateContenuto(contenuto.id, { rimossoDaProgrammazione: false });
                            await loadContenuti();
                            setRefreshTrigger(prev => prev + 1);
                          } catch (error) {
                            console.error('Errore nel rimandare il post in programmazione:', error);
                          }
                        }}
                        className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0 col-span-2"
                        title="Era stato tolto dai contenuti da programmare: lo rimette in coda in Pubblicazione"
                      >
                        <Calendar size={12} className="flex-shrink-0" />
                        Rimanda in programmazione
                      </button>
                    )}
                    {/* Nero su bianco con bordo nero: il pulsante principale della card.
                        Colori in hex per non cadere nelle rimappature di index.css. */}
                    {contenuto.stato === StatoContenuto.BOZZA && (
                      <button
                        onClick={() => handleMarkAsReady(contenuto)}
                        className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0 col-span-2"
                        title="Il cliente ha confermato il testo: passa a Testo pronto"
                      >
                        <CheckCircle size={12} className="flex-shrink-0" />
                        <span className="hidden sm:inline">Testo confermato dal cliente</span>
                        <span className="sm:hidden">Confermato</span>
                      </button>
                    )}
                    {/* Modifica, Archivia ed Elimina sulla stessa riga; Archivia
                        manca sui contenuti già in archivio o pubblicati. */}
                    <div className="col-span-2 flex gap-2">
                      <button
                        onClick={() => handleEditContent(contenuto)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0"
                        title="Modifica il contenuto"
                        aria-label="Modifica contenuto"
                      >
                        <Edit size={12} className="flex-shrink-0" />
                        Modifica
                      </button>
                      {contenuto.stato !== StatoContenuto.ARCHIVIATO && contenuto.stato !== StatoContenuto.PUBBLICATO && (
                        <button
                          onClick={() => handleArchive(contenuto)}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0"
                          title="Metti il contenuto in archivio: lo ritrovi nel tab Script Archiviati"
                          aria-label="Archivia contenuto"
                        >
                          <Archive size={12} className="flex-shrink-0" />
                          Archivia
                        </button>
                      )}
                      {/* Elimina: stesso stile di "Segna come pronto", con l'etichetta e non solo il cestino */}
                      <button
                        onClick={async () => {
                          if (window.confirm('Sei sicuro di voler eliminare questo contenuto?')) {
                            try {
                              await deleteContenuto(contenuto.id);
                              await loadContenuti();
                              setRefreshTrigger(prev => prev + 1);
                            } catch (error) {
                              console.error('Errore nell\'eliminazione:', error);
                              alert('Errore nell\'eliminazione del contenuto. Riprova.');
                            }
                          }
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#ffffff] hover:bg-[#13161C] text-[#13161C] hover:text-[#ffffff] border border-[#13161C] text-[11px] font-medium rounded-md transition-colors min-h-[36px] sm:min-h-[32px] min-w-0"
                        title="Elimina definitivamente il contenuto"
                        aria-label="Elimina contenuto"
                      >
                        <Trash2 size={12} className="flex-shrink-0" />
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      </>
      )}

      {/* Rubriche / Format */}
      {activeTab === 'rubriche' && (
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
            <p className="text-xs sm:text-sm text-slate-500">
              Rubriche e format ricorrenti che stiamo utilizzando con {selectedClient?.nomeAzienda} ({rubriche.length})
            </p>
            <Button
              onClick={handleNewRubrica}
              variant="primary"
              className="min-h-[44px] w-full sm:w-auto text-sm sm:text-base"
            >
              <Plus size={18} />
              <span>Nuova Rubrica</span>
            </Button>
          </div>

          {isLoadingRubriche ? (
            <Card className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-[#fdf751] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-sm text-slate-500">Caricamento rubriche...</p>
            </Card>
          ) : rubriche.length === 0 ? (
            <Card className="p-4 sm:p-6 md:p-8 text-center">
              <BookOpen className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                Nessuna rubrica o format
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                Aggiungi le rubriche e i format che state usando con questo cliente
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {rubriche.map((rubrica) => (
                <Card key={rubrica.id} className="p-3 sm:p-4 md:p-6">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium uppercase tracking-wide border ${
                      rubrica.tipo === 'format'
                        ? 'bg-violet-500/15 text-violet-300 border-violet-500/30'
                        : 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                    }`}>
                      {rubrica.tipo === 'format' ? <LayoutTemplate size={11} /> : <BookOpen size={11} />}
                      {rubrica.tipo === 'format' ? 'Format' : 'Rubrica'}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleEditRubrica(rubrica)}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-300 transition-colors"
                        title="Modifica"
                        aria-label="Modifica rubrica"
                      >
                        <Edit size={15} />
                        <span>Modifica</span>
                      </button>
                      <button
                        onClick={() => handleDeleteRubrica(rubrica.id)}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/15 border border-red-500/25 transition-colors"
                        title="Elimina"
                        aria-label="Elimina rubrica"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                    {rubrica.titolo}
                  </h3>
                  {rubrica.descrizione && (
                    <p className="mt-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                      {rubrica.descrizione}
                    </p>
                  )}
                  {(rubrica.gancio || rubrica.corpo || rubrica.cta) && (
                    <dl className="mt-3 space-y-1.5 border-t border-slate-100 dark:border-gray-700 pt-3">
                      {([['Gancio', rubrica.gancio], ['Corpo', rubrica.corpo], ['CTA', rubrica.cta]] as const)
                        .filter(([, testo]) => testo)
                        .map(([etichetta, testo]) => (
                          <div key={etichetta} className="text-xs sm:text-sm">
                            <dt className="inline font-semibold uppercase tracking-wide text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 mr-1.5">{etichetta}</dt>
                            <dd className="inline text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{testo}</dd>
                          </div>
                        ))}
                    </dl>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Creative / Linee guida — un solo pannello piatto, sezioni separate da linee sottili */}
      {activeTab === 'creative' && (() => {
        const areaFlat =
          'block w-full px-3.5 py-3 text-sm sm:text-[15px] leading-relaxed bg-[#F4F5F7] border-0 rounded-lg resize-y ' +
          'text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#DDE0E6] transition-colors';
        const labelFlat = 'flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-2';
        return (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
              <p className="text-xs sm:text-sm text-slate-500">
                Linee guida per l'editing dei reel di {selectedClient?.nomeAzienda}, dettate dal cliente
              </p>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {creativeSaved && (
                  <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-emerald-600 font-medium">
                    <CheckCircle size={14} /> Salvato
                  </span>
                )}
                <Button
                  onClick={handleSaveCreative}
                  disabled={isSavingCreative}
                  variant="primary"
                  className="min-h-[44px] w-full sm:w-auto text-sm"
                >
                  <Save size={16} />
                  <span>{isSavingCreative ? 'Salvando...' : 'Salva linee guida'}</span>
                </Button>
              </div>
            </div>

            {isLoadingCreative ? (
              <Card className="p-6 text-center">
                <div className="w-6 h-6 border-2 border-[#fdf751] border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="mt-2 text-sm text-slate-500">Caricamento linee guida...</p>
              </Card>
            ) : (
              <Card padding="none" className="divide-y divide-[#EBEDF1]">
                {/* Obiettivi di comunicazione — la cornice strategica: guida anche l'AI Assistant */}
                <section className="p-4 sm:p-6">
                  <label className={`${labelFlat} text-slate-500`}>
                    <Target size={13} /> Obiettivi di comunicazione
                  </label>
                  <textarea
                    rows={6}
                    value={creativeForm.obiettivi}
                    onChange={(e) => updateCreativeField('obiettivi', e.target.value)}
                    className={areaFlat}
                    placeholder="Cosa devono ottenere i contenuti: farsi conoscere in zona, portare prenotazioni, costruire autorevolezza, far crescere la community..."
                  />
                  <p className="mt-2 text-xs text-slate-400">
                    L'AI Assistant scarta le idee che non servono a questi obiettivi.
                  </p>
                </section>

                {/* Taglio / stile */}
                <section className="p-4 sm:p-6">
                  <label className={`${labelFlat} text-slate-500`}>
                    <Film size={13} /> Taglio / stile dei reel
                  </label>
                  <textarea
                    rows={4}
                    value={creativeForm.taglio}
                    onChange={(e) => updateCreativeField('taglio', e.target.value)}
                    className={areaFlat}
                    placeholder="Ritmo del montaggio, tipo di tagli, inquadrature, transizioni, mood generale..."
                  />
                </section>

                {/* Cosa valorizzare / Cosa evitare, affiancati da lg in su */}
                <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 p-4 sm:p-6">
                  <div>
                    <label className={`${labelFlat} text-emerald-600`}>
                      <CheckCircle size={13} /> Cosa valorizzare
                    </label>
                    <textarea
                      rows={5}
                      value={creativeForm.da_dire}
                      onChange={(e) => updateCreativeField('da_dire', e.target.value)}
                      className={areaFlat}
                      placeholder="Messaggi chiave, tono di voce, parole/claim da usare, argomenti da valorizzare..."
                    />
                  </div>
                  <div>
                    <label className={`${labelFlat} text-red-500`}>
                      <X size={13} /> Cosa evitare
                    </label>
                    <textarea
                      rows={5}
                      value={creativeForm.da_non_dire}
                      onChange={(e) => updateCreativeField('da_non_dire', e.target.value)}
                      className={areaFlat}
                      placeholder="Argomenti da evitare, parole vietate, competitor da non citare, toni da non usare..."
                    />
                  </div>
                </section>

                {/* Altre linee guida */}
                <section className="p-4 sm:p-6">
                  <label className={`${labelFlat} text-slate-500`}>
                    <FileText size={13} /> Altre linee guida
                  </label>
                  <textarea
                    rows={4}
                    value={creativeForm.note}
                    onChange={(e) => updateCreativeField('note', e.target.value)}
                    className={areaFlat}
                    placeholder="Font, musica, sottotitoli, logo/watermark, formati, hashtag ricorrenti, riferimenti..."
                  />
                </section>
              </Card>
            )}
          </div>
        );
      })()}

      {/* Drive clip — cartelle e organizzazione delle clip dal Google Drive */}
      {activeTab === 'drive' && (
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-slate-500">
                Cartelle e organizzazione delle clip di {selectedClient?.nomeAzienda} sul Drive
              </p>
              {driveMatched && driveFolderName && (
                <p className="mt-0.5 text-[11px] sm:text-xs text-slate-400 inline-flex items-center gap-1">
                  <Folder size={12} /> Cartella abbinata:{' '}
                  <span className="font-medium text-slate-600 dark:text-slate-300">{driveFolderName}</span>
                </p>
              )}
            </div>
            {driveFolderLink && (
              <a
                href={driveFolderLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                <ExternalLink size={14} /> Apri in Drive
              </a>
            )}
          </div>

          {/* Breadcrumb di navigazione */}
          {driveMatched && drivePath.length > 0 && (
            <div className="flex items-center flex-wrap gap-1 mb-3 text-xs sm:text-sm">
              {drivePath.map((p, i) => (
                <React.Fragment key={p.id}>
                  {i > 0 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                  <button
                    onClick={() => { if (i < drivePath.length - 1) openDriveFolder(p, i); }}
                    disabled={i === drivePath.length - 1 || isLoadingDrive}
                    className={`px-1.5 py-0.5 rounded truncate max-w-[160px] ${
                      i === drivePath.length - 1
                        ? 'text-slate-900 dark:text-slate-100 font-medium'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {p.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {isLoadingDrive ? (
            <Card className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-[#fdf751] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-sm text-slate-500">Caricamento dal Drive...</p>
            </Card>
          ) : driveError ? (
            <Card className="p-6 text-center">
              <AlertTriangle className="mx-auto h-8 w-8 text-red-400" />
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{driveError}</p>
            </Card>
          ) : driveMatched === false ? (
            <Card className="p-4 sm:p-6 md:p-8 text-center">
              <Folder className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                Nessuna cartella Drive trovata
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                Non ho trovato una cartella per "{selectedClient?.nomeAzienda}" nel Drive "Lista Clienti Social".
              </p>
            </Card>
          ) : driveItems.length === 0 ? (
            <Card className="p-4 sm:p-6 md:p-8 text-center">
              <Folder className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                Cartella vuota
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">Questa cartella non contiene ancora elementi.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {driveItems.map((item) => item.isFolder ? (
                <button
                  key={item.id}
                  onClick={() => openDriveFolder({ id: item.id, name: item.name })}
                  disabled={isLoadingDrive}
                  className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-md transition-all text-left"
                >
                  <Folder className="w-5 h-5 text-amber-400 flex-shrink-0" fill="currentColor" />
                  <span className="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{item.name}</span>
                </button>
              ) : (
                <a
                  key={item.id}
                  href={item.webViewLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-md transition-all"
                >
                  {item.mimeType.startsWith('image/') ? (
                    <Image className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  ) : item.mimeType.startsWith('video/') ? (
                    <Video className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{item.name}</span>
                  <ExternalLink size={12} className="text-slate-400 flex-shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Form Nuovo/Modifica Contenuto — pagina intera (niente popup) */}
      {isModalOpen && (
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button
            onClick={handleCloseModal}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            title="Indietro"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
              {selectedContenuto ? 'Modifica Contenuto' : 'Nuovo Contenuto'}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">{selectedClient?.nomeAzienda}</p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          {/*
            Layout responsive:
            - mobile (default): tutto stack verticale con space-y-4 (invariato)
            - desktop lg+: griglia 2-col compatta per metterci tutto in un'unica schermata orizzontale.
              Hook/Body/Copy del post/Copertina spannano 2 col per leggibilità.
          */}
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-5 lg:gap-y-4">
                {/* Rubriche e format del cliente — abbreviazioni da usare nel titolo */}
                {rubriche.length > 0 && (
                  <div className="lg:col-span-2 rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50/60 dark:bg-blue-900/15 p-3 sm:p-4">
                    {/* Su mobile l'intestazione è un tasto apri/chiudi; da sm in su è solo un titolo. */}
                    <button
                      type="button"
                      onClick={() => setRubricheAperte(prev => !prev)}
                      className="w-full flex items-center justify-between gap-2 text-left sm:pointer-events-none"
                      aria-expanded={rubricheAperte}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <BookOpen size={16} className="text-blue-600 dark:text-blue-300 flex-shrink-0" />
                        <span className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.01em' }}>
                          Rubriche
                        </span>
                        <span className="sm:hidden text-xs text-slate-500 dark:text-slate-400">({rubriche.length})</span>
                      </span>
                      <ChevronDown
                        size={18}
                        className={`sm:hidden flex-shrink-0 text-slate-400 transition-transform ${rubricheAperte ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Chiuso su mobile: solo le sigle, così restano sott'occhio senza occupare mezzo schermo */}
                    {!rubricheAperte && (
                      <div className="flex flex-wrap gap-1.5 mt-2 sm:hidden">
                        {rubriche.map((r) => (
                          <span
                            key={r.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                              r.tipo === 'format'
                                ? 'bg-violet-500/15 text-violet-600 dark:text-violet-300 border-violet-500/30'
                                : 'bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30'
                            }`}
                          >
                            {r.tipo === 'format' ? <LayoutTemplate size={12} className="flex-shrink-0" /> : <BookOpen size={12} className="flex-shrink-0" />}
                            {r.titolo}
                          </span>
                        ))}
                      </div>
                    )}

                    <p className={`text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3 ${rubricheAperte ? '' : 'hidden sm:block'}`}>
                      Abbreviazioni delle rubriche e dei format che usiamo con {selectedClient?.nomeAzienda}: indicale nel titolo dello script.
                    </p>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${rubricheAperte ? '' : 'hidden sm:grid'}`}>
                      {rubriche.map((r) => (
                        <div
                          key={r.id}
                          className="flex flex-col items-start gap-1.5 sm:flex-row sm:gap-2.5 p-2.5 rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                        >
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-semibold border flex-shrink-0 ${
                              r.tipo === 'format'
                                ? 'bg-violet-500/15 text-violet-600 dark:text-violet-300 border-violet-500/30'
                                : 'bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30'
                            }`}
                          >
                            {r.tipo === 'format' ? <LayoutTemplate size={13} className="flex-shrink-0" /> : <BookOpen size={13} className="flex-shrink-0" />}
                            {r.titolo}
                          </span>
                          {r.descrizione && (
                            <span className="text-xs text-slate-600 dark:text-slate-300 leading-snug sm:pt-1">{r.descrizione}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tipo di contenuto: reel (hook/corpo/CTA, riprese, montaggio),
                    post grafico (carosello in computer grafica) o carosello foto
                    (foto reali scattate nel locale, 03/11/2026). I due post: solo
                    copy + immagini, dritti in programmazione appena il testo è
                    confermato; in post.bymarke.com li fanno «Post grafici» e
                    «Caroselli & foto». */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Tipo di contenuto</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {([
                      { chiave: 'reel', valore: 'reel', formato: 'grafica', etichetta: 'Reel', descrizione: 'Video: hook, corpo e CTA', Icona: Video },
                      { chiave: 'grafica', valore: 'post', formato: 'grafica', etichetta: ETICHETTA_FORMATO.grafica, descrizione: 'Carosello in computer grafica', Icona: Image },
                      { chiave: 'foto', valore: 'post', formato: 'foto', etichetta: ETICHETTA_FORMATO.foto, descrizione: 'Foto reali scattate nel locale', Icona: Camera },
                    ] as { chiave: string; valore: TipoContenuto; formato: FormatoPost; etichetta: string; descrizione: string; Icona: typeof Video }[]).map(({ chiave, valore, formato, etichetta, descrizione, Icona }) => {
                      const attivo = formData.tipo === valore && (valore === 'reel' || formData.formatoPost === formato);
                      return (
                        <button
                          key={chiave}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, tipo: valore, formatoPost: formato, campagnaAdv: valore === 'post' ? false : prev.campagnaAdv }))}
                          className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                            attivo
                              ? 'border-[#13161C] bg-[#13161C]'
                              : 'border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-slate-300'
                          }`}
                        >
                          <Icona size={18} className={`flex-shrink-0 ${attivo ? 'text-[#fdf751]' : 'text-slate-400'}`} />
                          <span className="min-w-0">
                            {/* colore sullo span: index.css tinge gli span nudi */}
                            <span className={`block text-sm font-semibold ${attivo ? 'text-[#ffffff]' : 'text-slate-900 dark:text-slate-100'}`}>{etichetta}</span>
                            <span className={`block text-xs ${attivo ? 'text-[#cbd5e1]' : 'text-slate-500 dark:text-slate-400'}`}>{descrizione}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {formData.tipo === 'post' && selectedClient && !selectedClient.postGrafici && (
                    <p className="mt-1.5 text-xs text-amber-600">
                      La scheda di {selectedClient.nomeAzienda} non prevede post: il {ETICHETTA_FORMATO[formData.formatoPost].toLowerCase()} si può comunque creare, ma il calendario non lo alterna ai reel.
                    </p>
                  )}
                  {/* La scheda prevede post, ma non di questo formato (03/11/2026: post grafici e caroselli foto contati a parte) */}
                  {formData.tipo === 'post' && selectedClient?.postGrafici && (() => {
                    const foto = Math.min(selectedClient.caroselliFotoSettimana || 0, selectedClient.postGraficiSettimana || 0);
                    const grafici = (selectedClient.postGraficiSettimana || 0) - foto;
                    const zero = formData.formatoPost === 'foto' ? foto === 0 : grafici === 0;
                    return zero ? (
                      <p className="mt-1.5 text-xs text-amber-600">
                        La scheda di {selectedClient.nomeAzienda} non prevede {formData.formatoPost === 'foto' ? 'caroselli foto' : 'post grafici'} ({grafici} post grafici e {foto} caroselli foto a settimana): si può comunque creare.
                      </p>
                    ) : null;
                  })()}
                </div>

                {/* Profilo di destinazione: solo per i clienti che pubblicano su
                    più profili dallo stesso piano (142 Restaurant e Bistrot).
                    Un secondo clic sulla scelta attiva la toglie. */}
                {(() => {
                  const profili = accountDelCliente(state.clienti.find(c => c.id === formData.clienteId));
                  if (profili.length === 0) return null;
                  const scelti = formData.accountDestinazione.filter(a => profili.includes(a));
                  const opzioni = [
                    ...profili.map(p => ({ chiave: p, etichetta: p, valore: [p] })),
                    { chiave: '__tutti', etichetta: profili.length === 2 ? 'Entrambi' : 'Tutti', valore: profili },
                  ];
                  return (
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Per quale profilo</label>
                      <div className={`grid gap-2 ${opzioni.length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
                        {opzioni.map(({ chiave, etichetta, valore }) => {
                          const attivo = scelti.length === valore.length && valore.every(v => scelti.includes(v));
                          return (
                            <button
                              key={chiave}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, accountDestinazione: attivo ? [] : valore }))}
                              className={`px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                                attivo
                                  ? 'border-[#13161C] bg-[#13161C] text-[#ffffff]'
                                  : 'border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-slate-300 text-slate-900 dark:text-slate-100'
                              }`}
                            >
                              {etichetta}
                            </button>
                          );
                        })}
                      </div>
                      {scelti.length === 0 && (
                        <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">Non ancora indicato</p>
                      )}
                    </div>
                  );
                })()}

                {/* Campagna ADV toggle (solo per i reel) */}
                {formData.tipo !== 'post' && (
                <div className="lg:col-span-2">
                  <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    formData.campagnaAdv
                      ? 'border-pink-400 bg-pink-50 dark:bg-pink-900/20'
                      : 'border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.campagnaAdv}
                      onChange={(e) => setFormData(prev => ({ ...prev, campagnaAdv: e.target.checked }))}
                      className="w-5 h-5 rounded accent-pink-500 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                        <Megaphone size={15} className="text-pink-500 flex-shrink-0" /> È un video Campagna ADV?
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Non è un classico reel da pubblicare</p>
                    </div>
                  </label>
                </div>
                )}

                {/* Casella "Cliente" nascosta: il cliente è già noto dal contesto.
                    Manteniamo comunque il valore come variabile (hidden input +
                    formData.clienteId) perché serve al salvataggio del contenuto. */}
                <input type="hidden" name="clienteId" value={formData.clienteId} />

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Titolo</label>
                  <input
                    type="text"
                    required
                    value={formData.titolo}
                    onChange={(e) => setFormData(prev => ({ ...prev, titolo: e.target.value }))}
                    className="mt-1 block w-full px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder={formData.tipo === 'post' ? 'Titolo del post' : 'Titolo del reel'}
                  />
                </div>

                {/* Hook, corpo e CTA: solo per i reel */}
                {formData.tipo !== 'post' && (
                <>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Hook</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.hook}
                    onChange={(e) => setFormData(prev => ({ ...prev, hook: e.target.value }))}
                    className="mt-1 block w-full min-h-[120px] sm:min-h-0 px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder="Gancio iniziale per catturare l'attenzione"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Corpo del testo</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    className="mt-1 block w-full min-h-[260px] sm:min-h-0 px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder="Contenuto principale del reel"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Call to Action</label>
                  <input
                    type="text"
                    required
                    value={formData.cta}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta: e.target.value }))}
                    className="mt-1 block w-full px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder="Invito all'azione"
                  />
                </div>
                </>
                )}

                <div className={formData.tipo === 'post' ? 'lg:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Link di ispirazione (opzionale)</label>
                  <input
                    type="url"
                    value={formData.linkIspirazione}
                    onChange={(e) => setFormData(prev => ({ ...prev, linkIspirazione: e.target.value }))}
                    className="mt-1 block w-full px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder="https://esempio.com"
                  />
                </div>

                {/* Copy del post: per il post grafico è il testo stesso del contenuto */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {formData.tipo === 'post' ? 'Copy' : 'Copy del post (opzionale)'}
                  </label>
                  <textarea
                    rows={4}
                    required={formData.tipo === 'post'}
                    value={formData.copyPost}
                    onChange={(e) => setFormData(prev => ({ ...prev, copyPost: e.target.value }))}
                    className="mt-1 block w-full min-h-[180px] sm:min-h-0 px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder={formData.tipo === 'post' ? (formData.formatoPost === 'foto' ? 'Testo del carosello: è tutto quello che va pubblicato insieme alle foto' : 'Testo del post grafico: è tutto quello che va pubblicato insieme all\'immagine') : 'Testo della caption che apparira sotto il post su Instagram...'}
                  />
                </div>

                {/* Copertina upload con drag & drop. Per un reel nuovo non c'è (03/11/2026, Michel): la
                    copertina la fa post.bymarke.com quando il reel è montato; resta nella modifica di un reel. */}
                {(formData.tipo === 'post' || selectedContenuto) && (
                <div className="border-t border-slate-200 pt-4 mt-4 lg:mt-0 lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                    <Image size={14} />
                    {formData.tipo === 'post' ? (formData.formatoPost === 'foto' ? 'Prima foto del carosello' : 'Immagine del post') : 'Copertina Reel (opzionale)'}
                  </label>

                  {formData.copertinaUrl ? (
                    <div className="space-y-2">
                      <div className="relative rounded-xl overflow-hidden border border-slate-200 max-w-[160px]" style={{ aspectRatio: formData.tipo === 'post' ? '4/5' : '9/16' }}>
                        <img
                          src={formData.copertinaUrl}
                          alt="Copertina"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, copertinaUrl: '' }))}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Rimuovi copertina
                      </button>
                    </div>
                  ) : (
                    <div
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleCoverUpload(file);
                      }}
                    >
                      <label className="block cursor-pointer">
                        <div className={`border-2 border-dashed rounded-xl p-5 sm:p-8 text-center transition-all ${
                          isUploadingCover
                            ? 'border-[#fdf751] bg-yellow-50'
                            : isDragging
                              ? 'border-[#fdf751] bg-yellow-50/50 scale-[1.02]'
                              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                        }`}>
                          {isUploadingCover ? (
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 border-2 border-[#fdf751] border-t-transparent rounded-full animate-spin" />
                              <p className="text-sm font-medium text-slate-600">Caricamento in corso...</p>
                            </div>
                          ) : (
                            <>
                              <div className={`mx-auto w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-colors ${
                                isDragging ? 'bg-yellow-100' : 'bg-slate-100'
                              }`}>
                                <Image className={`h-6 w-6 sm:h-7 sm:w-7 transition-colors ${isDragging ? 'text-yellow-600' : 'text-slate-400'}`} />
                              </div>
                              <p className="mt-3 text-sm sm:text-base font-medium text-slate-700">
                                {isDragging ? 'Rilascia qui' : formData.tipo === 'post' ? (formData.formatoPost === 'foto' ? 'Trascina qui la prima foto' : 'Trascina qui l\'immagine del post') : 'Trascina qui la copertina'}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                                oppure <span className="text-[#c4b800] font-medium">clicca per selezionare</span>
                              </p>
                              <p className="text-[10px] sm:text-xs text-slate-400 mt-2">JPG, PNG, WebP - max 10MB</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          disabled={isUploadingCover}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleCoverUpload(file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
                )}
              </div>

          {/* Footer */}
          <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3">
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="primary"
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Salvando...' : (selectedContenuto ? 'Aggiorna' : 'Crea Contenuto')}
            </Button>
            <Button
              type="button"
              onClick={handleCloseModal}
              variant="secondary"
              className="w-full sm:w-auto"
            >
              Annulla
            </Button>
          </div>
        </form>
      </div>
      )}

      {/* AI Assistant a tutto schermo: è un editor, sopra menù e barra mobile.
          Si torna al Copywriting dal pulsante in alto (o con Esc). */}
      {activeTab === 'assistant' && selectedClientId && selectedClient && createPortal(
        <div
          className="fixed inset-0 flex flex-col"
          style={{ zIndex: 9999 /* sopra la barra mobile (9997) */, backgroundColor: '#FAFAFB', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid #EBEDF1' }}>
            <button
              onClick={() => setActiveTab('current')}
              className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
              title="Torna al Copywriting (Esc)"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-sm sm:text-base font-semibold text-slate-900 truncate" style={{ letterSpacing: '-0.02em' }}>AI Assistant</div>
              <div className="text-[11px] sm:text-xs text-slate-500 truncate">{selectedClient.nomeAzienda} · Copywriting</div>
            </div>
          </div>
          <div className="flex-1 min-h-0 px-3 sm:px-5 py-3">
            <CopywriterAssistant
              fullscreen
              clientLogoUrl={selectedClient.logoUrl}
              clientId={selectedClientId}
              clientName={selectedClient.nomeAzienda}
              getToken={getToken}
              onDraftSaved={() => {
                loadContenuti();
                setRefreshTrigger(prev => prev + 1);
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {activeTab === 'intel' && selectedClientId && selectedClient && (
        <NewsCompetitor clientId={selectedClientId} clientName={selectedClient.nomeAzienda} />
      )}

      {activeTab === 'calendario' && selectedClientId && selectedClient && (
        <CalendarioCliente clientId={selectedClientId} clientName={selectedClient.nomeAzienda} />
      )}

      {activeTab === 'caption' && selectedClientId && selectedClient && (
        <CaptionMancanti clientId={selectedClientId} clientName={selectedClient.nomeAzienda} onConteggio={setCaptionMancanti} />
      )}

      {/* AI Import Modal */}
      <AIImportModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        preselectedClientId={selectedClientId || undefined}
        onSuccess={() => {
          loadContenuti();
          setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Modal Rubrica / Format */}
      <AnimatedModal
        isOpen={isRubricaModalOpen}
        onClose={handleCloseRubricaModal}
        title={selectedRubrica ? 'Modifica Rubrica' : 'Nuova Rubrica'}
        size="md"
      >
        <form onSubmit={handleSubmitRubrica} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRubricaForm(prev => ({ ...prev, tipo: 'rubrica' }))}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                  rubricaForm.tipo === 'rubrica'
                    ? 'border-[#fdf751] bg-[#fdf751]/10 text-slate-900 dark:text-slate-100'
                    : 'border-slate-200 dark:border-gray-600 text-slate-500 hover:border-slate-300'
                }`}
              >
                <BookOpen size={15} /> Rubrica
              </button>
              <button
                type="button"
                onClick={() => setRubricaForm(prev => ({ ...prev, tipo: 'format' }))}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                  rubricaForm.tipo === 'format'
                    ? 'border-[#fdf751] bg-[#fdf751]/10 text-slate-900 dark:text-slate-100'
                    : 'border-slate-200 dark:border-gray-600 text-slate-500 hover:border-slate-300'
                }`}
              >
                <LayoutTemplate size={15} /> Format
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Titolo</label>
            <input
              type="text"
              required
              value={rubricaForm.titolo}
              onChange={(e) => setRubricaForm(prev => ({ ...prev, titolo: e.target.value }))}
              className="mt-1 block w-full px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
              placeholder="Nome della rubrica o del format"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Descrizione (opzionale)</label>
            <textarea
              rows={5}
              value={rubricaForm.descrizione}
              onChange={(e) => setRubricaForm(prev => ({ ...prev, descrizione: e.target.value }))}
              className="mt-1 block w-full px-4 py-3 text-base bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
              placeholder="Come funziona, struttura, cadenza, note utili..."
            />
          </div>

          {/* Struttura vincolante: se compilata, l'AI scrive sempre GANCIO / CORPO / CTA
              come indicato qui, anche quando la richiesta suona da POV o sketch. */}
          <div className="rounded-xl border border-slate-200 dark:border-gray-600 p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Struttura dei contenuti (opzionale)</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Se compili anche uno solo di questi campi, l'AI Assistant scrive i contenuti di questa rubrica sempre con
                gancio, corpo e CTA fatti così: niente POV o scene senza chiusura.
              </p>
            </div>
            {([
              { campo: 'gancio', etichetta: 'Gancio', segnaposto: 'Es. una domanda diretta o un falso mito da smontare nei primi 3 secondi' },
              { campo: 'corpo', etichetta: 'Corpo', segnaposto: 'Es. una sola spiegazione tecnica sviluppata bene, con un esempio pratico' },
              { campo: 'cta', etichetta: 'CTA', segnaposto: 'Es. una domanda finale che invita a commentare, mai «scrivimi in DM»' },
            ] as const).map(({ campo, etichetta, segnaposto }) => (
              <div key={campo}>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{etichetta}</label>
                <textarea
                  rows={2}
                  value={rubricaForm[campo]}
                  onChange={(e) => setRubricaForm(prev => ({ ...prev, [campo]: e.target.value }))}
                  className="mt-1 block w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                  placeholder={segnaposto}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3">
            <Button type="submit" disabled={isSubmittingRubrica} variant="primary" className="w-full sm:w-auto">
              {isSubmittingRubrica ? 'Salvando...' : (selectedRubrica ? 'Aggiorna' : 'Crea Rubrica')}
            </Button>
            <Button type="button" onClick={handleCloseRubricaModal} variant="secondary" className="w-full sm:w-auto">
              Annulla
            </Button>
          </div>
        </form>
      </AnimatedModal>

    </div>
  );
};

export default Copywriter;
